import { useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import {
  DeleteFormTemplateDocument,
  DraftCustomTemplateDocument,
  PublishCustomTemplateDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import PublishedQuestions from 'modules/trainings/components/Revamp/Form/Questions/Published'
import EditTrainingFormModal from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/TrainingFormDetail/EditTrainingForm'
import { TrainingFormContextType } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/TrainingFormDetail/types'

const TemplateQuestions = () => {
  const { trainingForm } = useOutletContext<TrainingFormContextType>()
  const [openEditTrainingFormModal, setOpenEditTrainingFormModal] = useState<boolean>(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [publishTemplate] = useMutation(PublishCustomTemplateDocument, {
    extractorFn: (data) => data.formsMutations.publishTemplateFormsForm,
  })
  const [deleteFormTemplate] = useMutation(DeleteFormTemplateDocument, {
    extractorFn: (data) => data.formsMutations.deleteFormsForm,
  })

  const [draftFormTemplate] = useMutation(DraftCustomTemplateDocument, {
    extractorFn: (data) => data.formsMutations.draftTemplateFormsForm,
  })
  const onDraftTemplate = async (id: string, resourceType: string) => {
    const result = await draftFormTemplate({ variables: { id, resourceType } })
    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    )

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }
  const onPublishTemplate = async (id: string, resourceType: string) => {
    const result = await publishTemplate({ variables: { id, resourceType } })
    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    )

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }
  const onDelete = async (id: string) => {
    const response = await deleteFormTemplate({
      variables: {
        id: id ?? '',
      },
    })
    return handleResult(response, () => {})
  }

  const handleActionClick = async (key: string): Promise<void> => {
    switch (key) {
      case 'publish':
        if (trainingForm.status === 'draft') {
          onPublishTemplate(trainingForm.id, trainingForm.resourceType)
        } else {
          onDraftTemplate(trainingForm.id, trainingForm.resourceType)
        }
        break
      case 'edit':
        const editPath = navigation.trainings.list.surveyTemplates.templateDetails.edit.fullPath({
          templateId: String(trainingForm.id),
        })
        navigate(editPath)
        break
      case 'delete':
        const result = await onDelete(trainingForm.id)
        if (result.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t(
              'forms.custom_templates.data_collection.delete_custom_template.success'
            ),
          })
          navigate(navigation.trainings.list.surveyTemplates.list.fullPath())
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('forms.custom_templates.data_collection.delete_custom_template.error'),
          })
        }
        break
      case 'edit_settings':
        setOpenEditTrainingFormModal(true)
        break
    }
  }
  return (
    <>
      <ResponsivePage.Body onActionClick={handleActionClick}>
        <PublishedQuestions />
        {trainingForm && (
          <EditTrainingFormModal
            onClose={() => setOpenEditTrainingFormModal(false)}
            onSuccess={() => {
              setOpenEditTrainingFormModal(false)
              app.setFlash({
                type: 'correct',
                message: i18n.t('trainings.surveys.create.edit_modal.form_success_message'),
              })
            }}
            onError={() => {
              setOpenEditTrainingFormModal(false)
              app.setFlash({
                type: 'wrong',
                message: i18n.t('trainings.surveys.create.edit_modal.form_error_message'),
              })
            }}
            isOpen={openEditTrainingFormModal}
            trainingForm={trainingForm}
          />
        )}
        <Outlet context={{ trainingForm }} />
      </ResponsivePage.Body>
    </>
  )
}
export default TemplateQuestions
